import { createReducer, Action, on } from '@ngrx/store';
import { omit } from 'lodash-es';

import { SimilarParagraphActions } from './similar-paragraph.actions';
import { ExpandedDeal, SimilarParagraphState } from './similar-paragraph.state';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';
import { DealActions } from '../deal/deal.actions';

export const initialState: SimilarParagraphState = {
  loading: false,
  expandedDeals: {},
  sort: { active: 'score', direction: 'desc' },
  showDiff: true,
  showBaseline: false,
  groupByDeal: true,
};

/** Settles a deal's expansion, unless it was collapsed or a new search cleared it meanwhile. */
const settleExpansion = (state: SimilarParagraphState, transactionId: string, expansion: ExpandedDeal): SimilarParagraphState =>
  state.expandedDeals[transactionId] ? { ...state, expandedDeals: { ...state.expandedDeals, [transactionId]: expansion } } : state;

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(SimilarParagraphActions.SearchSimilarParagraphs, (state, action) => ({
      ...state,
      loading: true,
      error: undefined,
      similarParagraphs: [],
      searchedDealCount: undefined,
      scanWithoutLimit: !!action.scanWithoutLimit,
      expandedDeals: {},
      originalParagraph: action.searchText || state.originalParagraph,
    })),
    on(SimilarParagraphActions.SearchSimilarParagraphsSuccess, (state, action) => ({
      ...state,
      loading: false,
      similarParagraphs: action.list,
      searchedDealCount: action.searchedDealCount,
      // New results default back to the diff view (mirrors the legacy reset-on-results behaviour).
      showDiff: action.list?.length ? true : state.showDiff,
    })),
    on(SimilarParagraphActions.SearchSimilarParagraphsFail, (state, action) => ({
      ...state,
      loading: false,
      error: action.error,
    })),
    on(SimilarParagraphActions.ExpandDeal, (state, { transactionId }) => ({
      ...state,
      expandedDeals: { ...state.expandedDeals, [transactionId]: { loading: true, paragraphs: [] } },
    })),
    on(SimilarParagraphActions.ExpandDealSuccess, (state, { transactionId, list }) =>
      settleExpansion(state, transactionId, { loading: false, paragraphs: list }),
    ),
    on(SimilarParagraphActions.ExpandDealFail, (state, { transactionId, error }) =>
      settleExpansion(state, transactionId, { loading: false, error, paragraphs: [] }),
    ),
    on(SimilarParagraphActions.CollapseDeal, (state, { transactionId }) => ({
      ...state,
      expandedDeals: omit(state.expandedDeals, transactionId),
    })),
    on(SimilarParagraphActions.SetSort, (state, action) => ({ ...state, sort: action.sort })),
    on(SimilarParagraphActions.SetShowDiff, (state, action) => ({ ...state, showDiff: action.showDiff })),
    on(SimilarParagraphActions.SetShowBaseline, (state, action) => ({ ...state, showBaseline: action.showBaseline })),
    on(SimilarParagraphActions.SetGroupByDeal, (state, action) => ({ ...state, groupByDeal: action.groupByDeal })),
    on(DealActions.LoadDealSuccess, (state) => ({ ...initialState, groupByDeal: state.groupByDeal })),
  ),
);

export function reducer(state: SimilarParagraphState | undefined, action: Action) {
  return featureReducer(state, action);
}
